/**
 * CANDIDATE IMPORT — read a recruiter's spreadsheet (.xlsx or .csv) into name + email rows.
 *
 * Runs in the browser (the import modal) and in unit tests. Deliberately dependency-free: an .xlsx
 * is a zip of XML parts, and every supported runtime ships `DecompressionStream("deflate-raw")`, so
 * the only code here is a zip directory walk and a cell reader. The popular npm `xlsx` package is
 * unmaintained on npm with open advisories; a full workbook library is far more than two columns need.
 *
 * Nothing here decides who is a candidate. It turns a file into rows and GUESSES which columns hold
 * the name and the email; the recruiter sees both guesses in the review modal and can change them.
 * Old binary .xls (pre-2007) is not a zip and is refused with a plain "save it as .xlsx or .csv".
 */
const EMAIL_IN_TEXT = /[\w.+'-]+@[\w-]+(?:\.[\w-]+)+/;
const EMAIL_EXACT = /^[\w.+'-]+@[\w-]+(?:\.[\w-]+)*\.[a-z]{2,}$/i;
export class ImportFileError extends Error {
}
// ── CSV ──────────────────────────────────────────────────────────────────────
/** RFC-4180 CSV (quoted fields, doubled quotes, CRLF or LF). Also accepts ; or tab when that is the separator. */
export function parseCsv(text) {
    const src = text.replace(/^﻿/, "");
    const firstLine = src.slice(0, src.search(/\r?\n|$/));
    const counts = [",", ";", "\t"].map((d) => [d, firstLine.split(d).length - 1]);
    const sep = counts.reduce((a, b) => (b[1] > a[1] ? b : a))[1] > 0 ? counts.reduce((a, b) => (b[1] > a[1] ? b : a))[0] : ",";
    const rows = [];
    let row = [];
    let cell = "";
    let quoted = false;
    for (let i = 0; i < src.length; i++) {
        const ch = src[i];
        if (quoted) {
            if (ch === '"') {
                if (src[i + 1] === '"') {
                    cell += '"';
                    i++;
                }
                else
                    quoted = false;
            }
            else
                cell += ch;
        }
        else if (ch === '"' && cell === "")
            quoted = true;
        else if (ch === sep) {
            row.push(cell);
            cell = "";
        }
        else if (ch === "\n" || ch === "\r") {
            if (ch === "\r" && src[i + 1] === "\n")
                i++;
            row.push(cell);
            rows.push(row);
            row = [];
            cell = "";
        }
        else
            cell += ch;
    }
    if (cell !== "" || row.length > 0) {
        row.push(cell);
        rows.push(row);
    }
    return rows.map((r) => r.map((c) => c.trim())).filter((r) => r.some((c) => c !== ""));
}
function zipEntries(buf) {
    const dv = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
    // End of central directory: signature 0x06054b50, searched backwards past any trailing comment.
    let eocd = -1;
    for (let i = buf.length - 22; i >= Math.max(0, buf.length - 22 - 0xffff); i--) {
        if (dv.getUint32(i, true) === 0x06054b50) {
            eocd = i;
            break;
        }
    }
    if (eocd < 0)
        throw new ImportFileError("This file isn't a readable .xlsx — save it as .xlsx or .csv and try again.");
    const count = dv.getUint16(eocd + 10, true);
    let p = dv.getUint32(eocd + 16, true);
    const out = [];
    const dec = new TextDecoder();
    for (let n = 0; n < count && p + 46 <= buf.length; n++) {
        if (dv.getUint32(p, true) !== 0x02014b50)
            break;
        const method = dv.getUint16(p + 10, true);
        const compressedSize = dv.getUint32(p + 20, true);
        const nameLen = dv.getUint16(p + 28, true);
        const extraLen = dv.getUint16(p + 30, true);
        const commentLen = dv.getUint16(p + 32, true);
        const localOffset = dv.getUint32(p + 42, true);
        out.push({ name: dec.decode(buf.subarray(p + 46, p + 46 + nameLen)), method, compressedSize, localOffset });
        p += 46 + nameLen + extraLen + commentLen;
    }
    return out;
}
async function inflateRaw(data) {
    const stream = new Response(data).body.pipeThrough(new DecompressionStream("deflate-raw"));
    return new Uint8Array(await new Response(stream).arrayBuffer());
}
async function readEntry(buf, e) {
    const dv = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
    const nameLen = dv.getUint16(e.localOffset + 26, true);
    const extraLen = dv.getUint16(e.localOffset + 28, true);
    const start = e.localOffset + 30 + nameLen + extraLen;
    const raw = buf.subarray(start, start + e.compressedSize);
    const bytes = e.method === 0 ? raw : e.method === 8 ? await inflateRaw(raw) : null;
    if (!bytes)
        throw new ImportFileError("This .xlsx uses a compression we can't read — save it as .csv and try again.");
    return new TextDecoder().decode(bytes);
}
const unescapeXml = (s) => s
    .replace(/&#x([0-9a-f]+);/gi, (_, h) => String.fromCodePoint(parseInt(h, 16)))
    .replace(/&#(\d+);/g, (_, d) => String.fromCodePoint(Number(d)))
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&amp;/g, "&");
/** All <t> text inside one fragment (rich text is several runs). */
const textOf = (xml) => [...xml.matchAll(/<(?:\w+:)?t(?:\s[^>]*)?>([\s\S]*?)<\/(?:\w+:)?t>/g)].map((m) => unescapeXml(m[1])).join("");
/** "BC12" → column index 54 (0-based). */
function colIndex(ref) {
    const letters = ref.match(/^[A-Z]+/i)?.[0].toUpperCase() ?? "A";
    let n = 0;
    for (const ch of letters)
        n = n * 26 + (ch.charCodeAt(0) - 64);
    return n - 1;
}
/** The first worksheet of an .xlsx workbook as rows of trimmed strings. */
export async function parseXlsx(data) {
    const buf = data instanceof Uint8Array ? data : new Uint8Array(data);
    if (buf[0] === 0xd0 && buf[1] === 0xcf) {
        throw new ImportFileError("That's an old Excel (.xls) file — open it in Excel and save it as .xlsx or .csv.");
    }
    const entries = zipEntries(buf);
    const byName = new Map(entries.map((e) => [e.name, e]));
    // First sheet in the workbook's own order: workbook.xml → its r:id → the rels target.
    let sheetPath;
    const wb = byName.get("xl/workbook.xml");
    const rels = byName.get("xl/_rels/workbook.xml.rels");
    if (wb && rels) {
        const rid = (await readEntry(buf, wb)).match(/<(?:\w+:)?sheet\b[^>]*\br:id="([^"]+)"/)?.[1];
        const relXml = await readEntry(buf, rels);
        const target = rid
            ? [...relXml.matchAll(/<Relationship\b[^>]*>/g)].map((m) => m[0]).find((t) => t.includes(`Id="${rid}"`))
                ?.match(/Target="([^"]+)"/)?.[1]
            : undefined;
        if (target)
            sheetPath = target.startsWith("/") ? target.slice(1) : `xl/${target.replace(/^\.\//, "")}`;
    }
    const sheet = (sheetPath && byName.get(sheetPath)) ??
        entries.filter((e) => /^xl\/worksheets\/[^/]+\.xml$/.test(e.name)).sort((a, b) => a.name.localeCompare(b.name))[0];
    if (!sheet)
        throw new ImportFileError("No worksheet found in that file — save it as .xlsx or .csv and try again.");
    const sst = byName.get("xl/sharedStrings.xml");
    const shared = sst ? [...(await readEntry(buf, sst)).matchAll(/<si>([\s\S]*?)<\/si>/g)].map((m) => textOf(m[1])) : [];
    const xml = await readEntry(buf, sheet);
    const rows = [];
    for (const rm of xml.matchAll(/<row\b[^>]*?(?:\/>|>([\s\S]*?)<\/row>)/g)) {
        const row = [];
        let next = 0;
        for (const cm of (rm[1] ?? "").matchAll(/<c\b([^>]*?)(?:\/>|>([\s\S]*?)<\/c>)/g)) {
            const attrs = cm[1] ?? "";
            const inner = cm[2] ?? "";
            const ref = attrs.match(/\br="([A-Z]+)\d+"/i)?.[1];
            const idx = ref ? colIndex(ref) : next;
            next = idx + 1;
            const type = attrs.match(/\bt="([^"]+)"/)?.[1];
            const v = inner.match(/<v>([\s\S]*?)<\/v>/)?.[1];
            let value = "";
            if (type === "s" && v !== undefined)
                value = shared[Number(v)] ?? "";
            else if (type === "inlineStr")
                value = textOf(inner);
            else if (v !== undefined)
                value = unescapeXml(v);
            row[idx] = value.trim();
        }
        rows.push(Array.from(row, (c) => c ?? ""));
    }
    return rows.filter((r) => r.some((c) => c !== ""));
}
/** Read a picked file by extension. */
export async function readSheet(file) {
    const lower = file.name.toLowerCase();
    if (lower.endsWith(".csv") || lower.endsWith(".txt")) {
        return parseCsv(new TextDecoder().decode(await file.arrayBuffer()));
    }
    if (lower.endsWith(".xlsx"))
        return parseXlsx(await file.arrayBuffer());
    if (lower.endsWith(".xls")) {
        throw new ImportFileError("That's an old Excel (.xls) file — open it in Excel and save it as .xlsx or .csv.");
    }
    throw new ImportFileError("Choose an Excel (.xlsx) or CSV file.");
}
// ── Which columns are the name and the email (and everything else we keep)? ─
/**
 * The detail columns an import understands besides name + email. Each is optional: a sheet with
 * just names and emails imports exactly as before. Values are what the RECRUITER's file says about
 * the person — third-party data, never scored or gated on, shown to the team as context.
 */
export const IMPORT_FIELDS = [
    "phone",
    "experienceYears",
    "noticePeriodDays",
    "lastWorkingDate",
    "currentCtc",
    "expectedCtc",
    "location",
];
export const IMPORT_FIELD_LABEL = {
    phone: "Phone",
    experienceYears: "Experience (years)",
    noticePeriodDays: "Notice period",
    lastWorkingDate: "Last working day",
    currentCtc: "Current CTC",
    expectedCtc: "Expected CTC",
    location: "Location",
};
const IS_EMAIL_HEAD = /^(e-?mail|email\s*(id|address)?|mail(\s*id)?|e-?mail\s*id|candidate\s*e-?mail|contact\s*e-?mail)$/i;
const IS_NAME_HEAD = /^((full|candidate|applicant|student|employee)\s*)?name$|^name\s*of\s*(the\s*)?candidate$/i;
const IS_FIRST_HEAD = /^(first|given)\s*name$|^fname$/i;
const IS_LAST_HEAD = /^(last|family|sur)\s*name$|^surname$|^lname$/i;
/** Header patterns per detail field, tried in this order so "expected CTC" never lands on current. */
const FIELD_HEADS = [
    ["expectedCtc", /\b(expected|exp(ec)?t?d?\.?)\s*(ctc|salary|package|pay|compensation)\b|\bectc\b/i],
    ["currentCtc", /\b(current|curr?\.?|present|existing)\s*(ctc|salary|package|pay|compensation)\b|\bcctc\b|^ctc$|^salary$|^package$/i],
    ["lastWorkingDate", /\b(last|final)\s*working\s*(day|date)\b|^lwd$|\brelieving\s*date\b/i],
    ["noticePeriodDays", /\bnotice(\s*period)?\b|^np$/i],
    ["experienceYears", /\b(total\s*|work\s*|overall\s*|relevant\s*)?(exp|experience|exp\.?)\b(\s*\(?\s*(in\s*)?(yrs?|years)\s*\)?)?$|^yoe$|^years?\s*of\s*experience$/i],
    ["phone", /\b(phone|mobile|mob|cell|contact)(\s*(no|number|num|#))?\.?$|^whats\s*app(\s*(no|number))?$/i],
    ["location", /\b(current\s*|preferred\s*)?(location|city|place|based\s*(in|at))$/i],
];
const norm = (s) => (s ?? "").replace(/[_*:.]/g, " ").replace(/\s+/g, " ").trim();
/** Detail columns named in a header row. A column already used for name / email is never reused. */
function guessFieldCols(heads, taken) {
    const out = {};
    const used = new Set(taken);
    for (const [field, re] of FIELD_HEADS) {
        const c = heads.findIndex((h, i) => !used.has(i) && h !== "" && re.test(h));
        if (c >= 0) {
            out[field] = c;
            used.add(c);
        }
    }
    return out;
}
export function guessColumns(rows) {
    // A header is one of the first five rows naming an email column.
    for (let r = 0; r < Math.min(5, rows.length); r++) {
        const heads = rows[r].map(norm);
        const emailCol = heads.findIndex((h) => IS_EMAIL_HEAD.test(h));
        if (emailCol < 0)
            continue;
        let nameCol = heads.findIndex((h) => IS_NAME_HEAD.test(h));
        let lastNameCol = -1;
        if (nameCol < 0) {
            nameCol = heads.findIndex((h) => IS_FIRST_HEAD.test(h));
            if (nameCol >= 0)
                lastNameCol = heads.findIndex((h) => IS_LAST_HEAD.test(h));
        }
        if (nameCol < 0)
            nameCol = heads.findIndex((h, i) => i !== emailCol && /name/i.test(h));
        const fields = guessFieldCols(heads, new Set([emailCol, nameCol, lastNameCol].filter((c) => c >= 0)));
        return { headerRow: r, nameCol, lastNameCol, emailCol, fields };
    }
    // No header: the email column is the one most full of addresses; the name is the first other
    // column that mostly holds text.
    const width = Math.max(0, ...rows.map((r) => r.length));
    const sample = rows.slice(0, 50);
    const score = (c, test) => sample.filter((r) => test(r[c] ?? "")).length;
    let emailCol = -1;
    let best = 0;
    for (let c = 0; c < width; c++) {
        const s = score(c, (v) => EMAIL_IN_TEXT.test(v));
        if (s > best)
            [best, emailCol] = [s, c];
    }
    let nameCol = -1;
    for (let c = 0; c < width; c++) {
        if (c === emailCol)
            continue;
        if (score(c, (v) => /[a-z]/i.test(v) && !EMAIL_IN_TEXT.test(v) && !/^\+?[\d\s()-]+$/.test(v)) >= sample.length / 2) {
            nameCol = c;
            break;
        }
    }
    return { headerRow: -1, nameCol, lastNameCol: -1, emailCol };
}
/** "5", "5 yrs", "4.5 years", "3-5" (→ 3), "5+" → years; nonsense → undefined. */
export function parseYears(v) {
    const t = v.trim().toLowerCase();
    if (!t)
        return undefined;
    const yrMo = t.match(/(\d+(?:\.\d+)?)\s*(?:y|yr|yrs|year|years)\b(?:\s*(\d+)\s*(?:m|mo|mos|month|months)\b)?/);
    if (yrMo)
        return Math.round((Number(yrMo[1]) + (yrMo[2] ? Number(yrMo[2]) / 12 : 0)) * 10) / 10;
    const mo = t.match(/^(\d+)\s*(?:m|mo|mos|month|months)$/);
    if (mo)
        return Math.round((Number(mo[1]) / 12) * 10) / 10;
    const n = t.match(/^(\d+(?:\.\d+)?)/);
    if (!n)
        return undefined;
    const y = Number(n[1]);
    return Number.isFinite(y) && y >= 0 && y <= 60 ? y : undefined;
}
/** "Immediate", "30", "30 days", "1 month", "2 months", "15 days or less" → days; unknown → undefined. */
export function parseNoticeDays(v) {
    const t = v.trim().toLowerCase();
    if (!t)
        return undefined;
    if (/immediate|serving|^0$|^nil$|^none$|joinable now|^no notice/.test(t))
        return 0;
    const m = t.match(/(\d+(?:\.\d+)?)\s*(d|day|days|w|wk|wks|week|weeks|m|mo|mos|month|months)?\b/);
    if (!m)
        return undefined;
    const n = Number(m[1]);
    const unit = m[2] ?? "d";
    const days = unit.startsWith("w") ? n * 7 : unit.startsWith("m") ? n * 30 : n;
    return days >= 0 && days <= 365 ? Math.round(days) : undefined;
}
/**
 * A date cell as YYYY-MM-DD: Excel serial numbers (the .xlsx reader returns the raw cell value),
 * ISO, and the Indian day-first forms ("05/11/2026", "5-Nov-2026", "5 Nov 2026"). Unparseable → undefined.
 */
export function parseSheetDate(v) {
    const t = v.trim();
    if (!t)
        return undefined;
    const iso = (y, m, d) => {
        const dt = new Date(Date.UTC(y, m - 1, d));
        if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== m - 1 || dt.getUTCDate() !== d)
            return undefined;
        if (y < 1990 || y > 2100)
            return undefined;
        return dt.toISOString().slice(0, 10);
    };
    if (/^\d{5}(\.\d+)?$/.test(t)) {
        // Excel's 1900 date system: serial 25569 = 1970-01-01.
        const dt = new Date(Math.round((Number(t) - 25569) * 86_400_000));
        return iso(dt.getUTCFullYear(), dt.getUTCMonth() + 1, dt.getUTCDate());
    }
    let m = t.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
    if (m)
        return iso(Number(m[1]), Number(m[2]), Number(m[3]));
    m = t.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2,4})$/);
    if (m) {
        const y = Number(m[3].length === 2 ? `20${m[3]}` : m[3]);
        return iso(y, Number(m[2]), Number(m[1])); // day-first, as written in India
    }
    const MONTHS = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
    m = t.match(/^(\d{1,2})[\s/-]*([a-z]{3})[a-z]*[\s/,-]*(\d{2,4})$/i);
    if (m) {
        const mon = MONTHS.indexOf(m[2].toLowerCase()) + 1;
        const y = Number(m[3].length === 2 ? `20${m[3]}` : m[3]);
        if (mon > 0)
            return iso(y, mon, Number(m[1]));
    }
    return undefined;
}
/** A phone cell, tidied: digits, a leading +, single spaces. Excel's "9.87654321E9" is expanded. */
export function parsePhone(v) {
    let t = v.trim();
    if (!t)
        return undefined;
    if (/^\d(\.\d+)?e\+?\d+$/i.test(t))
        t = Number(t).toFixed(0);
    t = t.replace(/[^\d+\s()-]/g, "").replace(/\s+/g, " ").trim();
    const digits = t.replace(/\D/g, "");
    return digits.length >= 7 && digits.length <= 15 ? t.slice(0, 40) : undefined;
}
/** A row-number column ("S.No", "Sr No", "#") — row numbering, not something about the person. */
const SERIAL_HEAD = /^(s\s*no|sr\s*no|sl\s*no|serial(\s*(no|number))?|no|#|row)$/i;
/** Apply a column choice to the rows. Emails are pulled out of the cell ("Asha <asha@x.com>") and lower-cased. */
export function toImportRows(rows, g) {
    const out = [];
    const header = g.headerRow >= 0 ? rows[g.headerRow] ?? [] : [];
    const fields = g.fields ?? {};
    const mapped = new Set([g.nameCol, g.lastNameCol, g.emailCol, ...Object.values(fields)].filter((c) => c !== undefined && c >= 0));
    const cellOf = (row, c) => (c !== undefined && c >= 0 ? (row[c] ?? "").trim() : "");
    for (let r = g.headerRow + 1; r < rows.length; r++) {
        const row = rows[r];
        const first = g.nameCol >= 0 ? row[g.nameCol] ?? "" : "";
        const last = g.lastNameCol >= 0 ? row[g.lastNameCol] ?? "" : "";
        const name = [first, last].filter(Boolean).join(" ").replace(/\s+/g, " ").trim();
        const cell = g.emailCol >= 0 ? row[g.emailCol] ?? "" : "";
        const email = (cell.match(EMAIL_IN_TEXT)?.[0] ?? cell).trim().toLowerCase();
        if (!name && !email)
            continue;
        const item = { line: r + 1, name, email };
        const phone = parsePhone(cellOf(row, fields.phone));
        if (phone)
            item.phone = phone;
        const years = parseYears(cellOf(row, fields.experienceYears));
        if (years !== undefined)
            item.experienceYears = years;
        const notice = parseNoticeDays(cellOf(row, fields.noticePeriodDays));
        if (notice !== undefined)
            item.noticePeriodDays = notice;
        const lwd = parseSheetDate(cellOf(row, fields.lastWorkingDate));
        if (lwd)
            item.lastWorkingDate = lwd;
        for (const k of ["currentCtc", "expectedCtc", "location"]) {
            const v = cellOf(row, fields[k]).slice(0, k === "location" ? 120 : 60);
            if (v)
                item[k] = v;
        }
        // Everything else the file says about them, under its own header — never silently dropped.
        if (header.length) {
            const extra = {};
            for (let c = 0; c < row.length; c++) {
                const head = norm(header[c]).slice(0, 60);
                const v = (row[c] ?? "").trim();
                if (mapped.has(c) || !head || !v || SERIAL_HEAD.test(head) || Object.keys(extra).length >= 20)
                    continue;
                extra[head] = v.slice(0, 300);
            }
            if (Object.keys(extra).length)
                item.extra = extra;
        }
        out.push(item);
    }
    return out;
}
export const isImportEmail = (email) => EMAIL_EXACT.test(email);
//# sourceMappingURL=candidateImport.js.map